///
/// EarthBrowser
///
/// Created by Matt Giger on 9/17/2026
/// Copyright (c) 2026 Geodata Labs
///
/// A user placemark pin on the globe: an EmberDome marker tinted with the
/// placemark's accent color, rendered at the placemark's anchor location.
/// Focus frames the ground (GroundFocusAdapter) like a city rather than
/// entering an entity-orbit camera - placemarks are places.
///

import { GroundFocusAdapter } from "@/earthbrowser/datasets/GroundFocusAdapter"
import { type InfoPanelData, type InfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { EmberDomeNode } from "@/earthbrowser/entities/EmberDome/EmberDomeNode"
import { geographic } from "@/earthbrowser/utils/geographic"
import { cDefaultPlacemarkColor, cPlacemarkColors, placemarkAnchor, type Placemark } from "@/redux/placemarksSlice"
import { haversineKm } from "@/utils/savedViews"
import { pushTargetToHistory } from "@/utils/navigationHistory"
import { type Camera } from "@stovokor/camera/Camera"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type FocusTransitionConfig } from "@stovokor/gestures/FocusTarget"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { trig } from "@stovokor/math"
import { rgba, type color } from "@stovokor/math/color"
import { matrix } from "@stovokor/math/matrix"
import { dot } from "@stovokor/math/vector"
import { PlacemarkMemberNode } from "./PlacemarkMemberNode"
import type { PlacemarkFeature } from "./PlacemarksLayer"

export const cPinHeightAboveTerrainKm = 0.02
// Physical dome size up close; far away the dome inflates to the angular floor
export const cMarkerSphereRadius = 0.5      // km
// Angular floor/ceiling (radians of screen-space radius): pins stay a clear
// discrete marker at all altitudes, slightly larger than webcams since a
// user's own places deserve prominence over ambient dataset markers
export const cPinMinAngular = 0.0025
export const cPinMaxAngular = 0.007
export const cPinFill = 0.85
export const cPinFarFill = 0.65
export const cHitPadding = 2.5

// Framing: a single pin frames like a small town; a multi-location placemark
// frames its members' spread. A member deep link (pin-<uuid>:<memberId>)
// retargets the framing onto that one member.
export const cSinglePinFramingKm = 4
const cMinFramingKm = 4
const cMaxFramingKm = 400
const cSpreadFramingFactor = 1.3

// Expansion: the anchor pin swaps for individual member pins once the
// members' angular spread (max anchor-member distance / camera distance)
// means they'd visibly separate on screen; hysteresis prevents flicker
// at the boundary. Framing a multi-location placemark lands well above
// the expand threshold, so a focused placemark always shows its members.
const cExpandAngular = 0.04
const cCollapseAngular = 0.025

/** Parse a palette hex color ("#e5484d") into an engine color */
export function paletteColor(key: string, alpha: number): color {
	const hex = cPlacemarkColors[key] ?? cPlacemarkColors[cDefaultPlacemarkColor]
	const r = parseInt(hex.slice(1, 3), 16) / 255
	const g = parseInt(hex.slice(3, 5), 16) / 255
	const b = parseInt(hex.slice(5, 7), 16) / 255
	return rgba(r, g, b, alpha)
}

/** Max distance (km) from the anchor to any member (0 for a single pin) */
function memberSpreadKm(placemark: Placemark): number {
	if (placemark.locations.length <= 1) return 0
	const anchor = placemarkAnchor(placemark)
	let maxKm = 0
	for (const location of placemark.locations) {
		const d = haversineKm(anchor.lat, anchor.lon, location.lat, location.lon)
		if (d > maxKm) maxKm = d
	}
	return maxKm
}

/** Framing radius (km) covering the placemark's member spread */
function framingRadiusKm(placemark: Placemark): number {
	if (placemark.locations.length <= 1) return cSinglePinFramingKm
	return Math.min(cMaxFramingKm, Math.max(cMinFramingKm, memberSpreadKm(placemark) * cSpreadFramingFactor))
}

export class PlacemarkNode extends GestureNode implements InfoPanelProvider {
	public readonly id: string
	public readonly focusable: boolean = true
	public feature: PlacemarkFeature
	public coordinate: geographic

	private ebContext: EarthBrowserContext
	private markerDome: EmberDomeNode
	private cameraAdapter: GroundFocusAdapter
	private elevationResolved = false
	private terrainVersion = -1
	private hovered = false
	private domeScale = cMarkerSphereRadius
	private spreadKm = 0
	private expanded = false
	private memberNodes: PlacemarkMemberNode[] = []

	constructor(context: EarthBrowserContext, feature: PlacemarkFeature) {
		super(context)
		this.id = `pin-${feature.id}`
		this.feature = feature
		this.ebContext = context
		this.spreadKm = memberSpreadKm(feature.placemark)

		const anchor = placemarkAnchor(feature.placemark)
		this.coordinate = new geographic(anchor.lon * trig.rad, anchor.lat * trig.rad, cPinHeightAboveTerrainKm)
		this.applyTerrainElevation()

		this.markerDome = new EmberDomeNode(context)
		this.markerDome.fillAlpha = cPinFill
		this.markerDome.farFillAlpha = cPinFarFill
		this.markerDome.minAngularSize = cPinMinAngular
		this.markerDome.transform = matrix.scale(cMarkerSphereRadius)
		this.updateMarkerColor()
		this.addNode(this.markerDome)

		// Ground focus: frame the place, hand control back to Earth navigation
		// (also wires the tap recognizer / EntityHoverHandler)
		this.cameraAdapter = new GroundFocusAdapter(
			context, this, this.coordinate.cartesian, framingRadiusKm(feature.placemark)
		)
	}

	private get placemark(): Placemark {
		return this.feature.placemark
	}

	// ========== Member focus (deep links: pin-<uuid>:<memberId>) ==========

	private focusMemberId?: string

	/**
	 * Aim the focus framing at one member location instead of the whole
	 * placemark. Cleared automatically when focus is lost so a later tap
	 * on the pin frames the whole place again. The anchor marker itself
	 * doesn't move - member pins arrive with the collections milestone.
	 */
	public setFocusMember(memberId: string | undefined): void {
		const member = memberId !== undefined
			? this.placemark.locations.find(location => location.id === memberId)
			: undefined
		this.focusMemberId = member?.id

		if (member) {
			const coordinate = new geographic(
				member.lon * trig.rad, member.lat * trig.rad,
				(member.altitudeM ?? 0) / 1000 + cPinHeightAboveTerrainKm
			)
			this.cameraAdapter.setTargetPosition(coordinate.cartesian)
			this.cameraAdapter.setFramingRadius(cSinglePinFramingKm)
		} else {
			this.cameraAdapter.setTargetPosition(this.coordinate.cartesian)
			this.cameraAdapter.setFramingRadius(framingRadiusKm(this.placemark))
		}
	}

	/**
	 * Sit the pin on the terrain (or at the member's explicit altitude).
	 * The elevation layer loads async; render() retries once it's ready,
	 * and re-seats the pin when the terrain exaggeration changes.
	 */
	private applyTerrainElevation(): void {
		const anchor = placemarkAnchor(this.placemark)
		let elevationKm: number
		this.terrainVersion = this.ebContext.terrainVersion
		if (anchor.altitudeM !== undefined) {
			elevationKm = anchor.altitudeM / 1000
			this.elevationResolved = true
		} else {
			elevationKm = this.ebContext.displayElevationKm(this.coordinate)
			this.elevationResolved = this.ebContext.baseElevation.loaded
		}
		this.coordinate = new geographic(
			this.coordinate.longitude,
			this.coordinate.latitude,
			elevationKm + cPinHeightAboveTerrainKm
		)
		this.transform = matrix.translate(this.coordinate.cartesian)
	}

	// ========== InfoPanelProvider ==========

	public getInfoPanelData(): InfoPanelData {
		const count = this.placemark.locations.length
		// A member deep link resolved from afar focuses the anchor node aimed
		// at that member; the callout should show the member, not the rollup
		const member = this.focusMemberId !== undefined
			? this.placemark.locations.find(location => location.id === this.focusMemberId)
			: undefined
		if (member) {
			return {
				title: member.name ?? this.placemark.name,
				subtitle: member.name ? this.placemark.name : undefined,
				theme: "placemark",
				contentType: "placemark",
				contentData: {
					type: "placemark",
					placemark: this.placemark,
					memberId: member.id,
				},
			}
		}
		return {
			title: this.placemark.name,
			subtitle: count > 1 ? `${count} locations` : undefined,
			theme: "placemark",
			contentType: "placemark",
			contentData: {
				type: "placemark",
				placemark: this.placemark,
			},
		}
	}

	// ========== Focus Lifecycle ==========

	public onFocusGained(camera: Camera): void {
		this.cameraAdapter.attach(camera)
		pushTargetToHistory("pin", this.focusMemberId !== undefined
			? `${this.placemark.id}:${this.focusMemberId}`
			: this.placemark.id)
	}

	public onFocusLost(): void {
		this.cameraAdapter.detach()
		this.setFocusMember(undefined)
	}

	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	public focusTransition(camera: Camera): FocusTransitionConfig {
		return {
			target: this.cameraAdapter.getTargetCameraState(camera),
			params: {
				duration: this.cameraAdapter.transitionDuration(camera),
				easing: "easeInOut",
			},
		}
	}

	// ========== Command Handlers ==========

	public handleCommand(command: string, data?: unknown): boolean {
		return this.cameraAdapter.handleCommand(command, data)
	}

	private updateMarkerColor(): void {
		if (this.hovered) {
			this.markerDome.color = paletteColor(this.placemark.color, 1)
			this.markerDome.fillAlpha = 1
		} else {
			this.markerDome.color = paletteColor(this.placemark.color, 0.9)
			this.markerDome.fillAlpha = cPinFill
		}
	}

	/** Hover cue from EntityHoverHandler: brighten the would-be-picked pin */
	public onHoverHighlight(hovered: boolean): void {
		this.hovered = hovered
		this.updateMarkerColor()
	}

	public updateFeature(feature: PlacemarkFeature): void {
		this.feature = feature
		this.spreadKm = memberSpreadKm(feature.placemark)

		const anchor = placemarkAnchor(feature.placemark)
		this.coordinate = new geographic(anchor.lon * trig.rad, anchor.lat * trig.rad, cPinHeightAboveTerrainKm)
		this.elevationResolved = false
		this.applyTerrainElevation()

		// Membership may have changed: drop the member pins and let the
		// expansion state machine rebuild them from fresh data next frame
		this.collapse()

		// Re-resolve the focused member (or re-aim at the moved anchor);
		// clears itself if the member was removed from the placemark
		this.setFocusMember(this.focusMemberId)
		this.updateMarkerColor()
	}

	// ========== Member expansion ==========

	/** Swap the anchor pin for individual member pins */
	private expand(): void {
		this.expanded = true
		this.markerDome.visible = false
		this.memberNodes = this.placemark.locations.map(member =>
			new PlacemarkMemberNode(this.ebContext, this, member))
		this.addNode(this.memberNodes)
	}

	/** Swap the member pins back for the single anchor pin */
	private collapse(): void {
		if (!this.expanded) return
		this.expanded = false
		this.markerDome.visible = true
		this.removeNode(this.memberNodes)
		this.memberNodes = []
	}

	/** The anchor's world position (member pins position relative to it) */
	public get anchorPosition() {
		return this.coordinate.cartesian
	}

	/** The live member pin for a member id, when expanded */
	public getMemberNode(memberId: string): PlacemarkMemberNode | undefined {
		return this.memberNodes.find(node => node.memberId === memberId)
	}

	override render(state: RenderState): RenderActionSet[] {
		if (!this.visible) {
			return []
		}

		// Nodes built before the async elevation layer finished loading sit
		// at sea level; snap onto the terrain once data is available, and
		// again whenever the terrain exaggeration changes
		if ((!this.elevationResolved || this.terrainVersion !== this.ebContext.terrainVersion) && this.ebContext.baseElevation.loaded) {
			this.applyTerrainElevation()
		}

		const distance = state.camera.position.subtract(this.transform.position).length

		// Expansion state machine: expand once the members would visibly
		// separate on screen (angular spread), collapse with hysteresis
		if (this.placemark.locations.length > 1) {
			const spreadAngular = this.spreadKm / Math.max(distance, 1)
			if (!this.expanded && spreadAngular > cExpandAngular) {
				this.expand()
			} else if (this.expanded && spreadAngular < cCollapseAngular) {
				this.collapse()
			}
		}

		// Apparent-size ceiling so the pin stays a discrete marker up close
		const targetScale = Math.min(cMarkerSphereRadius, distance * cPinMaxAngular)
		if (Math.abs(targetScale - this.domeScale) > this.domeScale * 0.02) {
			this.domeScale = targetScale
			this.markerDome.transform = matrix.scale(targetScale)
		}

		return super.render(state)
	}

	/**
	 * Apparent-size picking (same approach as WebcamNode): the pickable
	 * radius tracks what the marker actually renders at, and the reported
	 * distance is biased by the ray's miss so the nearest pin center wins
	 * when markers overlap.
	 */
	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		// While expanded the anchor pin is hidden; member pins take the picks
		if (this.expanded) {
			return undefined
		}

		const rayDepth = -dot(state.ray.origin, state.ray.direction)
		if (rayDepth <= 0) {
			return undefined
		}
		const missDistance = state.ray.pointAt(rayDepth).length

		const apparentRadius = Math.max(
			Math.min(cMarkerSphereRadius, rayDepth * cPinMaxAngular),
			rayDepth * cPinMinAngular
		)
		if (missDistance > apparentRadius * cHitPadding) {
			return undefined
		}

		return {
			node: this,
			hitPoint: state.ray.pointAt(rayDepth),
			distance: rayDepth + missDistance,
		}
	}
}
